/* eslint-disable prefer-destructuring */
import Cookies from 'js-cookie';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';

import gqlService from '@sellermodules/register/services/graphql';
import gqlLocation from '@modules/location/services/graphql';
import themeService from '@modules/theme/services/graphql';
import { getToken, getCustomer } from '@modules/login/services/graphql';

import { custDataNameCookie } from '@config';
import { setLogin } from '@helper_auth';
import { regexPhoneDial, regexEmail } from '@helper_regex';
import Layout from '@layout';
import { setLocalStorage } from '@helper_localstorage';

const ContentWrapper = (props) => {
    const {
        Content,
        storeLogo,
        t,
        recaptcha,
        createStore,
        isEnableTaxPayer,
        isEnableID,
    } = props;
    const router = useRouter();
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [userData, setUserData] = React.useState({});
    const [createSeller] = gqlService.createSeller();
    const [getCountries, getCountriesRes] = gqlLocation.getCountries();
    const [getCountry, getCountryRes] = gqlLocation.getCountry();
    const [getCityKecByRegionCode, getCityKecByRegionCodeRes] = gqlLocation.getCityKecByRegionCode();

    const [getAcl] = themeService.customerAccessControlListLazy();
    const [storeConfigMenu] = themeService.storeConfigMenu();

    const [getCustomerData] = getCustomer({
        onCompleted: async (res) => {
            try {
                if (res.customer.customer_company_code) {
                    const [resAcl] = await Promise.all([
                        getAcl(),
                    ]);
                    setLocalStorage('acl', JSON.stringify(resAcl?.data?.customerAccessControlList));
                } else {
                    const [resAcl, resConfigMenu] = await Promise.all([
                        getAcl(),
                        storeConfigMenu(),
                    ]);
                    setLocalStorage('acl', JSON.stringify(resAcl?.data?.customerAccessControlList));
                    setLocalStorage('config_acl', JSON.stringify({
                        pickpackWave: resConfigMenu?.data?.storeConfig?.swiftoms_pickpack_wave_enable,
                        pickpackBatch: resConfigMenu?.data?.storeConfig?.swiftoms_pickpack_batch_enable,
                        tada: resConfigMenu?.data?.storeConfig?.swiftoms_tada_enable,
                        vendor: resConfigMenu?.data?.storeConfig?.swiftoms_enable_vendor_portal,
                    }));
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
            } finally {
                Cookies.set('refreshAcl', 0);
                if (res.customer.customer_company_code) {
                    Cookies.set(custDataNameCookie, res.customer);
                    router.push('/seller/storesetting');
                } else {
                    router.push('/');
                }
            }
        },
        onError: (e) => {
            window.backdropLoader(false);
            window.toastMessage({
                open: true,
                text: e.message,
                variant: 'error',
            });
        },
    });
    const [getCustomerToken] = getToken();

    const [mapPosition, setMapPosition] = React.useState({
        lat: '-6.197361',
        lng: '106.774535',
    });

    const displayLocationInfo = (position) => {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;

        setMapPosition({
            lat,
            lng,
        });
    };
    const [dialCode, setDialCode] = React.useState('62');
    const useRegexPhone = regexPhoneDial(dialCode);

    const handleSubmit = async (input, captcha) => {
        createSeller({
            variables: { input },
            context: {
                request: 'internal',
                headers: {
                    'x-recaptcha': captcha,
                },
            },
        }).then((res) => {
            setUserData({ ...res.data.createSeller });
            window.backdropLoader(false);
            setIsSuccess(true);
        }).catch((e) => {
            window.backdropLoader(false);
            window.toastMessage({
                open: true,
                variant: 'error',
                text: t(`registerseller:${e.message.replaceAll(' ', '_').replace(/[.,/#!?$%^&*;:{}=\-`~()]/g, '')}`),
            });
        });
    };

    const recaptchaRef = React.createRef();
    const { sitekey } = recaptcha;
    const [validationAddress, setValidationAddress] = React.useState(({
        region: createStore && Yup.object().typeError(t('registerseller:This_is_a_Required_field'))
            .required(t('registerseller:This_is_a_Required_field')),
        city: createStore && Yup.object().typeError(t('registerseller:This_is_a_Required_field'))
            .required(t('registerseller:This_is_a_Required_field')),
        countries: createStore && Yup.object().typeError(t('registerseller:This_is_a_Required_field'))
            .required(t('registerseller:This_is_a_Required_field')),
    }));

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            password: '',
            password_confirmation: '',
            phone_number: '',
            store_name: '',
            region: null,
            city: null,
            countries: {
                __typename: 'Country',
                full_name_english: 'Indonesia',
                id: 'ID',
            },
            street: '',
            longitude: '',
            latitude: '',
            postcode: '',
            taxpayer_number: '',
            taxpayer_file: '',
            id_card_number: '',
            id_card_file: '',
            captcha: '',
            account_url: '/seller/account',
            confirm_url: '/user/account/confirm',
            login_url: '/login',
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(t('registerseller:This_is_a_Required_field')),
            email: Yup.string().required(t('registerseller:This_is_a_Required_field')).matches(regexEmail, t('registerseller:Invalid_email_format')),
            password: Yup.string().required(t('registerseller:This_is_a_Required_field')),
            password_confirmation: Yup.string().oneOf([Yup.ref('password'), null], t('registerseller:Passwords_do_not_match'))
                .required(t('registerseller:This_is_a_Required_field')),
            phone_number: Yup.string().nullable().matches(useRegexPhone, t('registerseller:Invalid_phone_number_format'))
                .required(t('registerseller:This_is_a_Required_field')),
            store_name: createStore && Yup.string().required(t('registerseller:This_is_a_Required_field')),
            street: createStore && Yup.string().required(t('registerseller:This_is_a_Required_field')),
            longitude: createStore && Yup.string().required(t('registerseller:This_is_a_Required_field')),
            latitude: createStore && Yup.string().required(t('registerseller:This_is_a_Required_field')),
            postcode: createStore && Yup.number().typeError(t('registerseller:Value_must_be_a_number'))
                .required(t('registerseller:This_is_a_Required_field')),
            taxpayer_number: isEnableTaxPayer && createStore && Yup.string().required(t('registerseller:This_is_a_Required_field')),
            taxpayer_file: isEnableTaxPayer && createStore && Yup.object().typeError(t('registerseller:This_is_a_Required_field'))
                .required(t('registerseller:This_is_a_Required_field')),
            id_card_number: isEnableID && createStore && Yup.number().typeError(t('registerseller:Value_must_be_a_number'))
                .required(t('registerseller:This_is_a_Required_field')),
            id_card_file: isEnableID && createStore && Yup.object().typeError(t('registerseller:This_is_a_Required_field'))
                .required(t('registerseller:This_is_a_Required_field')),
            captcha: recaptcha.enable && Yup.string().required(`Captcha ${t('registerseller:required')}`),
            ...validationAddress,
        }),
        validateOnChange: true,
        onSubmit: async (values) => {
            const {
                city, countries, latitude, longitude, postcode, region, store_name, street,
                taxpayer_number, taxpayer_file, id_card_number, id_card_file, captcha, address, ...restValues
            } = values;
            window.backdropLoader(true);
            let input = {
                ...restValues,
            };
            if (isEnableTaxPayer) {
                input = {
                    ...input,
                    taxpayer_number,
                    taxpayer_file: taxpayer_file.binary,
                };
            }
            if (isEnableID) {
                input = {
                    ...input,
                    id_card_number,
                    id_card_file: id_card_file.binary,
                };
            }
            if (createStore) {
                input.store = {
                    city: city?.value || '',
                    latitude: String(latitude),
                    longitude: String(longitude),
                    postcode,
                    region: region?.code || '',
                    name: store_name,
                    street,
                    country_id: countries.id,
                };
            }
            if (recaptcha.enable) {
                try {
                    await handleSubmit(input, captcha);
                } catch (e) {
                    window.backdropLoader(false);
                    window.toastMessage({
                        open: true,
                        variant: 'error',
                        text: t('registerseller:Could_not_verify_captcha'),
                    });
                }
                recaptchaRef.current.reset();
            } else {
                handleSubmit(input);
            }
        },
    });

    // eslint-disable-next-line consistent-return
    const handleLogin = async () => {
        if (userData?.status === 'pending') {
            return router.push('/login');
        }
        window.backdropLoader(true);
        return getCustomerToken({
            variables: {
                email: formik.values.email,
                password: formik.values.password,
            },
        }).then((res) => {
            const token = res.data.internalGenerateCustomerToken.token;
            if (token) {
                setLogin(1);
                getCustomerData();
            }
        }).catch((e) => {
            window.backdropLoader(false);
            if (e.message.search('graphql-customer-unconfirmed') >= 0) {
                router.push('/login');
            } else {
                window.toastMessage({
                    open: true,
                    variant: 'error',
                    text: e.message.split(':')[0],
                });
            }
        });
    };

    const handleDragPosition = (value) => {
        setMapPosition(value);
        formik.setFieldValue('longitude', value.lng);
        formik.setFieldValue('latitude', value.lat);
    };

    const handleDropFile = (files, name) => {
        const fileName = files[0].file.name;
        const { baseCode } = files[0];
        formik.setFieldValue(`${name}.filename`, fileName);
        formik.setFieldValue(`${name}.binary`, baseCode);
    };

    React.useMemo(() => {
        if (typeof window !== 'undefined' && navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(displayLocationInfo);
        }
    }, []);

    const contentProps = {
        ...props,
        formik,
        handleSubmit,
        getCountries,
        getCountriesRes,
        getCountry,
        getCountryRes,
        getCityKecByRegionCode,
        getCityKecByRegionCodeRes,
        storeLogo,
        t,
        setDialCode,
        sitekey,
        recaptchaRef,
        handleDragPosition,
        mapPosition,
        handleDropFile,
        isSuccess,
        userData,
        handleLogin,
        setValidationAddress,
    };

    return (
        <Content {...contentProps} />
    );
};

const Core = (props) => {
    const { t, storeConfig } = props;
    const pageConfig = {
        header: false,
        sidebar: false,
        title: t('registerseller:Register_Seller'),
    };

    const contentProps = {
        ...props,
        recaptcha: {
            enable: storeConfig?.recaptcha_for_create_new_account === 'invisible' || storeConfig?.recaptcha_for_create_new_account === 'recaptcha',
            sitekey: storeConfig?.recaptcha_for_create_new_account === 'invisible'
                ? storeConfig?.recaptcha_v2_invisible_public_key : storeConfig?.recaptcha_v2_public_key,
        },
        enableMap: storeConfig?.google_map_enabled,
        gmapKey: storeConfig?.google_maps_api_key || '',
        geocodingKey: storeConfig?.geocoding_api_key || '',
        createStore: storeConfig?.swiftoms_vendor_registration_enable_create_store,
        isEnableTaxPayer: storeConfig?.seller_enable_taxpayer ?? false,
        isEnableID: storeConfig?.seller_enable_id_card ?? false,
    };

    return (
        <Layout pageConfig={pageConfig}>
            <ContentWrapper
                {...contentProps}
            />
        </Layout>
    );
};

export default Core;
